import React, { useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { Permission, Role } from './types/rbac';
import PermissionGuard from './components/PermissionGuard';
import { PageLoader, MobileLoadingIndicator } from './components/ui';
import DashboardLayout from './layouts/DashboardLayout';
import { useAuth } from './contexts/AuthContext';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Users = React.lazy(() => import('./pages/Users'));
const ApiKeys = React.lazy(() => import('./pages/ApiKeys'));
const Analytics = React.lazy(() => import('./pages/Analytics'));

const Login = React.lazy(() => import('./pages/Login'));
const SignUp = React.lazy(() => import('./pages/SignUp'));
const EmailVerification = React.lazy(() => import('./pages/EmailVerification'));
const Playground = React.lazy(() => import('./pages/Playground'));
const DeveloperDocs = React.lazy(() => import('./pages/DeveloperDocs'));
const GetStarted = React.lazy(() => import('./pages/GetStarted'));
const PublicApp = React.lazy(() => import('./pages/PublicApp'));
const TeamManagement = React.lazy(() => import('./pages/TeamManagement'));
const UserManagement = React.lazy(() => import('./pages/UserManagement'));
const ModelManagement = React.lazy(() => import('./pages/ModelManagement'));
const AppTemplatesManagement = React.lazy(() => import('./pages/AppTemplatesManagement'));
const MyApps = React.lazy(() => import('./pages/MyApps'));
const AppLibrary = React.lazy(() => import('./pages/AppLibrary'));
const AppManagerLanding = React.lazy(() => import('./pages/AppManagerLanding'));
const DeployedAppView = React.lazy(() => import('./pages/DeployedAppView'));
const DeployAppView = React.lazy(() => import('./pages/DeployAppView'));
const Unauthorized = React.lazy(() => import('./pages/Unauthorized'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const LegalPages = React.lazy(() => import('./pages/LegalPages'));
const TestConfigForm = React.lazy(() => import('./pages/TestConfigForm'));

// Dashboard wrapper component to handle referral modal state
function DashboardWrapper() {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  return (
    <PrivateRoute>
      <DashboardLayout onOpenReferralModal={() => setIsReferralModalOpen(true)}>
        <DashboardRouteHandler isReferralModalOpen={isReferralModalOpen} onReferralModalClose={() => setIsReferralModalOpen(false)} />
      </DashboardLayout>
    </PrivateRoute>
  );
}

// Component to handle passing props to the Dashboard component
function DashboardRouteHandler({ isReferralModalOpen, onReferralModalClose }: { isReferralModalOpen: boolean, onReferralModalClose: () => void }) {
  const location = useLocation();

  // Only pass the props to the Dashboard component
  if (location.pathname === '/dashboard') {
    return (
      <PermissionGuard requiredPermissions={[Permission.VIEW_OWN_USAGE, Permission.VIEW_TEAM_USAGE, Permission.VIEW_ALL_USAGE]}>
        <Dashboard isReferralModalOpen={isReferralModalOpen} onReferralModalClose={onReferralModalClose} />
      </PermissionGuard>
    );
  }

  // For all other routes, just render the outlet
  return <Outlet />;
}

// Route guard for authenticated users
function PrivateRoute({ children }: { children: JSX.Element }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldRender, setShouldRender] = React.useState(false);

  React.useEffect(() => {
    // Redirect USER and APP_MANAGER if they're coming from login/registration
    // or if they're accessing the root path
    if (isAuthenticated && !isLoading && (user?.role === Role.USER || user?.role === Role.APP_MANAGER)) {
      const isFromAuth = location.state && (
        location.state.from === '/login' ||
        location.state.from?.startsWith('/register')
      );
      const isRootPath = location.pathname === '/';

      if ((isFromAuth || isRootPath) && location.pathname !== '/my-apps') {
        // Use navigate instead of Navigate component for better handling
        navigate('/my-apps', { replace: true });
        return;
      }
    }

    // If we reach here, we should render the children
    setShouldRender(true);
  }, [isAuthenticated, isLoading, user, location.pathname, navigate, location.state]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Only render children after we've determined we should
  if (!shouldRender) {
    return <PageLoader />;
  }

  return children;
}

// Get Started route component that works for both authenticated and non-authenticated users
function GetStartedRoute() {
  const { isAuthenticated } = useAuth();

  return isAuthenticated ? (
    <DashboardLayout>
      <GetStarted />
    </DashboardLayout>
  ) : (
    <DashboardLayout isGuestMode={true}>
      <GetStarted />
    </DashboardLayout>
  );
}

// Redirect component for referral links
function ReferralRedirect() {
  const location = useLocation();
  const referralCode = location.pathname.split('/').pop();

  // Check if the referral code is actually a valid role path
  const validRoles = ["app_manager"];
  if (referralCode && validRoles.includes(referralCode)) {
    // If it's a valid role path, redirect to the role-specific registration route
    return <Navigate to={`/register/${referralCode}`} replace />;
  }

  // Otherwise, treat it as a regular referral code
  return <Navigate to={`/register/${referralCode}`} replace />;
}

// Index redirect component that handles role-based redirects
function IndexRedirect() {
  const { user } = useAuth();

  // Redirect USER and APP_MANAGER to my-apps page
  if (user?.role === Role.USER || user?.role === Role.APP_MANAGER) {
    return <Navigate to="/my-apps" replace />;
  }

  // Redirect admin users to dashboard
  return <Navigate to="/dashboard" replace />;
}

// Create the router configuration
const router = createBrowserRouter([
  // Public routes
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/login/:referralCode",
    element: <Login />,
  },
  {
    path: "/register",
    element: <Login initialMode="register" />,
  },
  {
    path: "/register/app_manager",
    element: <Login initialMode="register" rolePath="app_manager" />,
  },
  {
    path: "/register/app_manager/:referralCode",
    element: <Login initialMode="register" rolePath="app_manager" />,
  },
  {
    path: "/register/:referralCode",
    element: <Login initialMode="register" />,
  },
  {
    path: "/referral/:referralCode",
    element: <ReferralRedirect />,
  },
  {
    path: "/verify-email/:token",
    element: <EmailVerification />,
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />,
  },
  {
    path: "/404",
    element: <NotFound />,
  },
  {
    path: "/get-started",
    element: <GetStartedRoute />,
  },
  {
    path: "/policy",
    element: <LegalPages />,
  },
  {
    path: "/apps/:slug",
    element: <PublicApp />,
  },

  // Protected routes
  {
    element: <DashboardWrapper />,
    children: [
      {
        path: "/",
        element: <IndexRedirect />,
      },
      {
        path: "/dashboard",
        element: <Outlet />,
      },
      {
        path: "/teams",
        element: (
          <PermissionGuard requiredPermissions={[Permission.MANAGE_TEAM_MEMBERS, Permission.MANAGE_ALL_TEAMS]}>
            <TeamManagement />
          </PermissionGuard>
        ),
      },
      {
        path: "/users",
        element: (
          <PermissionGuard requiredPermissions={[Permission.MANAGE_ALL_TEAMS]}>
            <Users />
          </PermissionGuard>
        ),
      },
      {
        path: "/api-keys",
        element: (
          <PermissionGuard requiredPermissions={[Permission.USE_API]}>
            <ApiKeys />
          </PermissionGuard>
        ),
      },
      {
        path: "/analytics",
        element: (
          <PermissionGuard requiredPermissions={[Permission.VIEW_TEAM_USAGE, Permission.VIEW_ALL_USAGE]}>
            <Analytics />
          </PermissionGuard>
        ),
      },

      {
        path: "/models",
        element: (
          <PermissionGuard requiredPermissions={[Permission.SYSTEM_CONFIGURATION]}>
            <ModelManagement />
          </PermissionGuard>
        ),
      },
      {
        path: "/playground",
        element: (
          <PermissionGuard requiredPermissions={[Permission.USE_API]}>
            <Playground />
          </PermissionGuard>
        ),
      },
      {
        path: "/app-templates-management",
        element: (
          <PermissionGuard requiredPermissions={[Permission.MANAGE_APP_STORE]}>
            <AppTemplatesManagement />
          </PermissionGuard>
        ),
      },
      {
        path: "/my-apps",
        element: (
          <PermissionGuard requiredPermissions={[Permission.USE_APP_STORE]}>
            <MyApps />
          </PermissionGuard>
        ),
      },
      {
        path: "/my-apps/:slug",
        element: (
          <PermissionGuard requiredPermissions={[Permission.USE_APP_STORE]}>
            <DeployedAppView />
          </PermissionGuard>
        ),
      },
      {
        path: "/app-manager",
        element: (
          <PermissionGuard requiredPermissions={[Permission.DEPLOY_APPS]}>
            <AppManagerLanding />
          </PermissionGuard>
        ),
      },
      {
        path: "/app-library",
        element: <Navigate to="/deploy-app/rag-chatbot" replace />,
      },
      {
        path: "/deploy-app/:templateSlug",
        element: (
          <PermissionGuard requiredPermissions={[Permission.DEPLOY_APPS]}>
            <DeployAppView />
          </PermissionGuard>
        ),
      },
      {
        path: "/docs",
        element: (
          <PermissionGuard requiredPermissions={[Permission.VIEW_DOCS]}>
            <DeveloperDocs />
          </PermissionGuard>
        ),
      },
      {
        path: "/test-config",
        element: (
          <PermissionGuard requiredPermissions={[Permission.DEPLOY_APPS]}>
            <TestConfigForm />
          </PermissionGuard>
        ),
      },

    ],
  },

  // Catch all route
  {
    path: "*",
    element: <Navigate to="/404" replace />,
  },
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  } as any,
});

export default router;
